import type { SourceRow, SourceStatus } from '../stores/source_activity'

// Pure view-model helpers for IngestActivityRow — what a Source row reads
// as in the Add-to-brain drawer's activity list (title, kind, outcome,
// status pill). Kept out of the SFC so each rule is unit-testable and
// the component stays a thin template.

// Row-title truncation: file previews stay short since the extracted text
// is secondary; notes get more room since the note text is what the user
// scans for.
export const INGEST_FILE_TITLE_MAX = 60
export const INGEST_NOTE_TITLE_MAX = 80

export interface IngestPill {
  label: string
  // Tinted-pill utility classes (`bg-<tone>/15 text-<tone>`).
  tone: string
}

const PILLS: Record<SourceStatus, IngestPill> = {
  queued: { label: 'Queued', tone: 'bg-surface-hover text-ink-muted' },
  running: { label: 'Ingesting', tone: 'bg-factory/15 text-factory' },
  succeeded: { label: 'Done', tone: 'bg-brand-primary/15 text-brand-primary' },
  failed: { label: 'Failed', tone: 'bg-status-alert/15 text-status-alert' },
}
const UNKNOWN_PILL: IngestPill = { label: 'Unknown', tone: 'bg-surface-hover text-ink-muted' }

export function ingestIsInFlight(status: SourceStatus | null): boolean {
  return status === 'queued' || status === 'running'
}

export function ingestStatusPill(status: SourceStatus | null): IngestPill {
  return status ? PILLS[status] : UNKNOWN_PILL
}

export function ingestKindLabel(kind: string): string {
  switch (kind) {
    case 'web_prompt':
      return 'Note'
    case 'file_upload':
      return 'File'
    case 'url':
      return 'URL'
    default:
      return kind
  }
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1).trim()}…` : text
}

export function ingestRowTitle(row: SourceRow): string {
  if (row.kind === 'url') return row.url || '(no url)'
  const text = (row.content ?? '').replaceAll(/\s+/g, ' ').trim()
  if (row.kind === 'file_upload') return text ? truncate(text, INGEST_FILE_TITLE_MAX) : '(uploaded file)'
  return truncate(text, INGEST_NOTE_TITLE_MAX) || '(empty note)'
}

function contentPagesCount(row: SourceRow): number {
  return (row.result?.pages_touched ?? []).filter((p) => p !== 'log.md').length
}

// One-line outcome for a terminal row: the error for a failure, otherwise
// what the run did (pages written, a question answered, a reminder, or
// nothing stored). Empty for in-flight rows and results not yet loaded.
export function ingestOutcome(row: SourceRow): string {
  if (row.status === 'failed') return row.error || 'Ingest failed'
  if (row.status !== 'succeeded' || !row.result) return ''
  const r = row.result
  if (r.reminder) return r.reminder.error || 'Reminder updated'
  if (r.routing === 'query' || r.answer) return 'Answered a question'
  const pages = contentPagesCount(row)
  if (pages > 0) return `${pages} page${pages === 1 ? '' : 's'} updated`
  return 'Nothing new to store'
}

// A run that succeeded but stored no content page (and wasn't a query
// answer or a reminder command) — the detail panel explains why.
function isNoOpIngest(row: SourceRow): boolean {
  const r = row.result
  return (
    row.status === 'succeeded' &&
    !!r &&
    r.routing !== 'query' &&
    !r.answer &&
    !r.reminder &&
    contentPagesCount(row) === 0
  )
}

// Whether the row has anything for ActivityRowDetail to show — mirrors
// that panel's own per-section conditions off the raw row fields so the
// expand toggle can decide before the panel is ever rendered.
export function ingestRowHasDetail(row: SourceRow): boolean {
  const body = row.kind === 'url' ? (row.url ?? '') : (row.content ?? '')
  const r = row.result
  return (
    body.trim().length > 0 ||
    ingestIsInFlight(row.status) ||
    (r?.answer?.length ?? 0) > 0 ||
    contentPagesCount(row) > 0 ||
    (r?.citations?.length ?? 0) > 0 ||
    isNoOpIngest(row) ||
    !!r?.reminder ||
    (row.error?.length ?? 0) > 0
  )
}
